import { Eye, EyeOff, Loader2, Check, AlertCircle, Bell, Mail, Receipt, FileCheck } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import { formatPhoneInputDisplay, cleanPhone } from '../../utils/userManagementFormatters'
import { validateEmail } from '../../utils/userManagementValidators'
import { inputClass, labelClass } from '../../utils/editPageUi'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import type { UserManagementPersonalInfoFormProps } from '../../types/components'

export function UserManagementPersonalInfoForm({
  firstName,
  setFirstName,
  lastName,
  setLastName,
  setLastNameTouched,
  phone,
  setPhone,
  email,
  setEmail,
  jobTitle,
  setJobTitle,
  refundActionNotif,
  setRefundActionNotif,
  refundActionEmail,
  setRefundActionEmail,
  invoiceActionNotif,
  setInvoiceActionNotif,
  invoiceActionEmail,
  setInvoiceActionEmail,
  formErrors,
  setFormErrors,
  submitting,
  getFirstNameError,
  getLastNameError,
  getPhoneError,
  onSubmit,
  onCancel,
  emailVerifyStatus,
  isActive,
  hasEmail,
  canTempPassword,
  canDisableEnable,
  showNotificationSettings,
  actionDetails,
  tempPassword,
  setTempPassword,
  showTempPwd,
  setShowTempPwd,
  settingTempPwd,
  togglingStatus,
  verifyingEmail,
  resettingPwd,
  onRequestTempPassword,
  onRequestAccountToggle,
  onVerifyEmail,
  onRequestResetPassword,
}: UserManagementPersonalInfoFormProps) {
  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate data-testid="um-edit-form">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-5 gap-y-4">
          <div className="min-w-0">
            <label className={labelClass}>First Name</label>
            <input
              type="text"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value)
                setFormErrors((prev) => ({ ...prev, first_name: '' }))
              }}
              onBlur={() => {
                const msg = getFirstNameError(firstName)
                setFormErrors((prev) => ({ ...prev, first_name: msg }))
              }}
              className={inputClass(formErrors.first_name)}
              data-testid="um-edit-first-name-input"
            />
            {formErrors.first_name && (
              <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-rose-600" data-testid="um-edit-first-name-error">
                <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />
                <span>{formErrors.first_name}</span>
              </div>
            )}
          </div>
          <div className="min-w-0">
            <label className={labelClass}>Last Name</label>
            <input
              type="text"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value)
                setFormErrors((prev) => ({ ...prev, last_name: '' }))
              }}
              onBlur={() => {
                setLastNameTouched(true)
                const msg = getLastNameError(lastName)
                setFormErrors((prev) => ({ ...prev, last_name: msg }))
              }}
              className={inputClass(formErrors.last_name)}
              data-testid="um-edit-last-name-input"
            />
            {formErrors.last_name && (
              <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-rose-600" data-testid="um-edit-last-name-error">
                <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />
                <span>{formErrors.last_name}</span>
              </div>
            )}
          </div>
          <div className="min-w-0">
            <label className={labelClass}>Phone</label>
            <input
              type="tel"
              inputMode="tel"
              placeholder="Phone"
              value={formatPhoneInputDisplay(phone)}
              onChange={(e) => {
                setPhone(cleanPhone(e.target.value))
                setFormErrors((prev) => ({ ...prev, phone: '' }))
              }}
              onBlur={() => {
                const msg = getPhoneError(phone)
                setFormErrors((prev) => ({ ...prev, phone: msg }))
              }}
              className={inputClass(formErrors.phone)}
              data-testid="um-edit-phone-input"
            />
            {formErrors.phone && (
              <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-rose-600" data-testid="um-edit-phone-error">
                <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />
                <span>{formErrors.phone}</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-5 gap-y-4">
          <div className="min-w-0">
            <label className={labelClass}>Job Title (optional)</label>
            <input
              type="text"
              placeholder="Job Title"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              className={inputClass()}
              data-testid="um-edit-job-title-input"
            />
          </div>

          {canTempPassword ? (
            <div className="min-w-0">
              <label className={labelClass}>Temp Password</label>
              <div className="flex items-stretch gap-2">
                <div className="relative min-w-0 flex-1">
                  <input
                    type={showTempPwd ? 'text' : 'password'}
                    placeholder="Set temporary password"
                    value={tempPassword}
                    onChange={(e) => setTempPassword(e.target.value)}
                    autoComplete="new-password"
                    className={cn(inputClass(), 'pr-10')}
                    data-testid="um-edit-temp-password-input"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowTempPwd((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showTempPwd ? 'Hide password' : 'Show password'}
                    data-testid="um-edit-temp-password-toggle-btn"
                  >
                    {showTempPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <button
                  type="button"
                  disabled={!tempPassword.trim() || settingTempPwd}
                  onClick={onRequestTempPassword}
                  className={cn(primaryEntityActionButtonClass, 'h-10 shrink-0 px-4 text-sm font-semibold min-w-0')}
                  data-testid="um-edit-set-temp-password-btn"
                >
                  {settingTempPwd && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}
                  Set Temp
                </button>
              </div>
            </div>
          ) : (
            <div className="min-w-0 hidden sm:block" aria-hidden />
          )}

          <div className="min-w-0">
            <label className={labelClass}>Email</label>
            <div className="flex items-stretch gap-2">
              <input
                type="text"
                inputMode="email"
                autoComplete="email"
                placeholder="Email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setFormErrors((prev) => ({ ...prev, email: '' }))
                }}
                onBlur={() => {
                  const val = email.trim()
                  if (!val) {
                    setFormErrors((prev) => ({ ...prev, email: 'Email is required.' }))
                  } else if (!validateEmail(val)) {
                    setFormErrors((prev) => ({ ...prev, email: 'Email is not valid.' }))
                  }
                }}
                className={cn(inputClass(formErrors.email), 'min-w-0 flex-1')}
                data-testid="um-edit-email-input"
              />
              <button
                type="button"
                disabled={verifyingEmail}
                onClick={() => {
                  const val = email.trim()
                  if (!val) {
                    setFormErrors((prev) => ({ ...prev, email: 'Email is required.' }))
                    return
                  }
                  if (!validateEmail(val)) {
                    setFormErrors((prev) => ({ ...prev, email: 'Email is not valid.' }))
                    return
                  }
                  setFormErrors((prev) => ({ ...prev, email: '' }))
                  void onVerifyEmail()
                }}
                className={cn(primaryEntityActionButtonClass, 'h-10 shrink-0 px-4 text-sm font-semibold min-w-0')}
                data-testid="um-edit-verify-email-btn"
              >
                {verifyingEmail && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}
                Verify Email
              </button>
            </div>
            {formErrors.email && (
              <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-rose-600" data-testid="um-edit-email-error">
                <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />
                <span>{formErrors.email}</span>
              </div>
            )}
          </div>

          <div className="min-w-0 self-start">
            <label className={labelClass}>Verification Status</label>
            <div className="flex h-10 items-center">
              <span
                className={cn(
                  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset',
                  emailVerifyStatus && emailVerifyStatus.toLowerCase().includes('verif')
                    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                    : 'bg-amber-50 text-amber-700 ring-amber-200'
                )}
                data-testid="um-edit-email-verify-status"
              >
                {emailVerifyStatus || 'Unverified'}
              </span>
            </div>
          </div>

          {canDisableEnable ? (
            <div className="min-w-0 self-start">
              <label className={labelClass}>Account Status</label>
              <div
                className={cn(
                  'inline-flex items-center rounded-lg bg-slate-100 p-1 border border-slate-200/80 text-xs font-medium',
                  togglingStatus && 'opacity-60 cursor-not-allowed'
                )}
                data-testid="um-edit-account-status-toggle-group"
              >
                <button
                  type="button"
                  disabled={togglingStatus}
                  onClick={() => {
                    if (!isActive) onRequestAccountToggle()
                  }}
                  data-testid="um-edit-status-active-toggle"
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-xs font-semibold transition-all duration-150 cursor-pointer',
                    isActive
                      ? 'bg-gradient-to-b from-[rgb(44_160_255)] to-[rgb(12_128_220)] text-white border border-[#0c80dc]/30 shadow-sm shadow-sky-500/20'
                      : 'bg-white text-slate-700 border border-slate-200/80 hover:bg-slate-50'
                  )}
                >
                  {isActive && <Check className="h-3.5 w-3.5 shrink-0" />}
                  <span>Active</span>
                </button>
                <button
                  type="button"
                  disabled={togglingStatus}
                  onClick={() => {
                    if (isActive) onRequestAccountToggle()
                  }}
                  data-testid="um-edit-status-inactive-toggle"
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-xs font-semibold transition-all duration-150 cursor-pointer',
                    !isActive
                      ? 'bg-gradient-to-b from-[rgb(44_160_255)] to-[rgb(12_128_220)] text-white border border-[#0c80dc]/30 shadow-sm shadow-sky-500/20'
                      : 'bg-white text-slate-700 border border-slate-200/80 hover:bg-slate-50'
                  )}
                >
                  {!isActive && <Check className="h-3.5 w-3.5 shrink-0" />}
                  <span>Not active</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="min-w-0 hidden sm:block" aria-hidden />
          )}
        </div>

        {showNotificationSettings && (
          <div className="rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/70 to-white p-4 sm:p-5 shadow-2xs space-y-4" data-testid="um-edit-notification-settings">
            <div className="flex items-center justify-between border-b border-slate-200/60 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-50 text-[rgb(21_151_255)] border border-sky-100 shadow-2xs">
                  <Bell className="h-4 w-4 shrink-0" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800">Notification Settings</h3>
                  <p className="text-xs text-slate-500">Configure alert and email notification preferences for system actions</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {actionDetails.contract_refund_approval && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-slate-200/80 bg-white p-3.5 sm:px-4 shadow-2xs transition-colors hover:border-slate-300">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-amber-50 text-amber-600 border border-amber-100 shrink-0">
                      <Receipt className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Contract Refund Approval</p>
                      <p className="text-xs text-slate-500">Alerts when a contract refund requires review or approval</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5 shrink-0 pl-11 sm:pl-0">
                    <label
                      className={cn(
                        'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold cursor-pointer transition-all select-none',
                        refundActionNotif
                          ? 'border-[rgb(21_151_255)] bg-sky-50 text-[rgb(12_128_220)] shadow-2xs ring-1 ring-[rgb(21_151_255)]/20'
                          : 'border-slate-200 bg-slate-50/50 text-slate-600 hover:bg-slate-100 hover:border-slate-300'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={refundActionNotif}
                        onChange={(e) => setRefundActionNotif(e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)] cursor-pointer"
                        data-testid="um-edit-refund-notif-checkbox"
                      />
                      <Bell className={cn('h-3.5 w-3.5 shrink-0', refundActionNotif ? 'text-[rgb(21_151_255)]' : 'text-slate-400')} />
                      <span>Send Notification</span>
                    </label>

                    <label
                      className={cn(
                        'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold cursor-pointer transition-all select-none',
                        refundActionEmail
                          ? 'border-[rgb(21_151_255)] bg-sky-50 text-[rgb(12_128_220)] shadow-2xs ring-1 ring-[rgb(21_151_255)]/20'
                          : 'border-slate-200 bg-slate-50/50 text-slate-600 hover:bg-slate-100 hover:border-slate-300'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={refundActionEmail}
                        onChange={(e) => setRefundActionEmail(e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)] cursor-pointer"
                        data-testid="um-edit-refund-email-checkbox"
                      />
                      <Mail className={cn('h-3.5 w-3.5 shrink-0', refundActionEmail ? 'text-[rgb(21_151_255)]' : 'text-slate-400')} />
                      <span>Send Email</span>
                    </label>
                  </div>
                </div>
              )}

              {actionDetails.contract_invoice_approval && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-slate-200/80 bg-white p-3.5 sm:px-4 shadow-2xs transition-colors hover:border-slate-300">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-emerald-50 text-emerald-600 border border-emerald-100 shrink-0">
                      <FileCheck className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Contract Invoice Approval</p>
                      <p className="text-xs text-slate-500">Alerts when a contract invoice requires review or approval</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5 shrink-0 pl-11 sm:pl-0">
                    <label
                      className={cn(
                        'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold cursor-pointer transition-all select-none',
                        invoiceActionNotif
                          ? 'border-[rgb(21_151_255)] bg-sky-50 text-[rgb(12_128_220)] shadow-2xs ring-1 ring-[rgb(21_151_255)]/20'
                          : 'border-slate-200 bg-slate-50/50 text-slate-600 hover:bg-slate-100 hover:border-slate-300'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={invoiceActionNotif}
                        onChange={(e) => setInvoiceActionNotif(e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)] cursor-pointer"
                        data-testid="um-edit-invoice-notif-checkbox"
                      />
                      <Bell className={cn('h-3.5 w-3.5 shrink-0', invoiceActionNotif ? 'text-[rgb(21_151_255)]' : 'text-slate-400')} />
                      <span>Send Notification</span>
                    </label>

                    <label
                      className={cn(
                        'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold cursor-pointer transition-all select-none',
                        invoiceActionEmail
                          ? 'border-[rgb(21_151_255)] bg-sky-50 text-[rgb(12_128_220)] shadow-2xs ring-1 ring-[rgb(21_151_255)]/20'
                          : 'border-slate-200 bg-slate-50/50 text-slate-600 hover:bg-slate-100 hover:border-slate-300'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={invoiceActionEmail}
                        onChange={(e) => setInvoiceActionEmail(e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)] cursor-pointer"
                        data-testid="um-edit-invoice-email-checkbox"
                      />
                      <Mail className={cn('h-3.5 w-3.5 shrink-0', invoiceActionEmail ? 'text-[rgb(21_151_255)]' : 'text-slate-400')} />
                      <span>Send Email</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="col-span-full flex flex-wrap items-center gap-3 pt-5 mt-5 border-t border-slate-100">
        <button
          type="submit"
          disabled={submitting || settingTempPwd || togglingStatus || verifyingEmail || resettingPwd}
          className={cn(primaryEntityActionButtonClass, 'px-6')}
          data-testid="um-edit-save-btn"
        >
          {(submitting || settingTempPwd || togglingStatus || verifyingEmail || resettingPwd) && (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          )}
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className={secondaryEntityActionButtonClass}
          data-testid="um-edit-cancel-btn"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!hasEmail || resettingPwd}
          onClick={onRequestResetPassword}
          className={cn(primaryEntityActionButtonClass, 'px-5 sm:ml-auto')}
          data-testid="um-edit-reset-password-btn"
        >
          {resettingPwd && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}
          Reset Password
        </button>
      </div>
    </form>
  )
}
